/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// v77.49 — Dedicated "Third-party access" page.
//
// Replaces the buried Settings → Third-party section as the primary
// entry point for parents to invite a solicitor / mediator /
// professional and manage their access. Lives at /app/third-party-access
// so the menu can deep-link straight to it without users having to
// scroll/search inside Settings.
//
// Feature coverage (mirrors the equivalent Settings section, but
// laid out for the standalone page):
//   • Create invite — duration, internal note, per-child scope,
//     view permissions (core + sensitive).
//   • List existing invites with status, expiry, accepted-by name,
//     children granted, permission count.
//   • Edit children, edit permissions, revoke, copy invite link.
//   • Activity log per accepted invite (third-party view audit).
//
// Backend dependencies: existing /api/mediator/invites + per-child
// scope + activity-log endpoints. No new backend work needed.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, UserCog, Copy, Calendar, ListChecks, Pencil, Users, ChevronLeft, Mail } from "lucide-react";
import { formatDT } from "@/lib/format";

const CORE_PERMS = [
  { key: "view_messages", label: "Messages", desc: "Read all conversations between co-parents." },
  { key: "view_calendar", label: "Calendar", desc: "View events, parenting schedule and notes." },
  { key: "view_expenses", label: "Expenses", desc: "View expense list, totals and balances." },
  { key: "view_upcoming_expenses", label: "Upcoming expenses", desc: "View flagged future costs and conversion status. Granted independently of Expenses." },
  { key: "view_documents", label: "Documents", desc: "View shared documents." },
  { key: "view_journal", label: "Journal", desc: "View shared journal entries." },
  { key: "view_requests", label: "Requests", desc: "View change requests + their decisions." },
];
const OPTIONAL_PERMS = [
  { key: "view_private_journal", label: "Private journal entries", desc: "Sensitive — only enable if explicitly required." },
  { key: "view_private_documents", label: "Private documents", desc: "Sensitive — only enable if explicitly required." },
  { key: "view_expense_details", label: "Full expense details", desc: "Receipts and full descriptions, not just totals." },
];
const ALL_PERMS = [...CORE_PERMS, ...OPTIONAL_PERMS];

function PermRow({ perm, value, onChange, testid }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#D9E1E3] last:border-b-0">
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#2E3A3F]">{perm.label}</div>
        <div className="text-xs text-[#6F828A]">{perm.desc}</div>
      </div>
    </div>
  );
}

function StatusBadge({ inv }) {
  if (inv.revoked) return <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[#9A2A23] bg-[#F8DDDA] border border-[#E8B8B2] rounded-sm px-1.5 py-0.5" data-testid={`invite-status-revoked-${inv.id}`}>Revoked</span>;
  if (inv.expires_at && inv.expires_at < new Date().toISOString()) return <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[#825A41] bg-[#FBF0DC] border border-[#E8C29A] rounded-sm px-1.5 py-0.5" data-testid={`invite-status-expired-${inv.id}`}>Expired</span>;
  if (inv.used_by_user_id) return <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[#116B6E] bg-[#D6F0EF] border border-[#A6D7D9] rounded-sm px-1.5 py-0.5" data-testid={`invite-status-active-${inv.id}`}>Active</span>;
  return <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5" data-testid={`invite-status-pending-${inv.id}`}>Pending acceptance</span>;
}

export default function ThirdPartyAccessPage() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create-invite form state
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [perms, setPerms] = useState({
    view_messages: true, view_calendar: true, view_expenses: true,
    view_upcoming_expenses: true,
    view_documents: true, view_journal: true, view_requests: true,
    view_private_journal: false, view_private_documents: false, view_expense_details: false,
  });
  const [childIds, setChildIds] = useState([]);
  const [creating, setCreating] = useState(false);

  // Edit dialogs
  const [editPermsFor, setEditPermsFor] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [editScopeFor, setEditScopeFor] = useState(null);
  const [editScopeIds, setEditScopeIds] = useState([]);
  const [viewsFor, setViewsFor] = useState(null);
  const [views, setViews] = useState([]);
  const [viewsLoading, setViewsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mi, ch] = await Promise.all([
        api.get("/mediator/invites").catch(() => ({ data: [] })),
        api.get("/children").catch(() => ({ data: [] })),
      ]);
      setInvites(mi.data || []);
      const childrenList = (ch.data || []).filter((c) => !c.archived_at);
      setChildren(childrenList);
      setChildIds((prev) => prev.length === 0 ? childrenList.map((c) => c.id) : prev);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (creating) return;
    setCreating(true);
    try {
      await api.post("/mediator/invites", {
        days: Number(days),
        note,
        permissions: perms,
        child_ids: childIds,
      });
      toast.success("Third-party invite created");
      setNote("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create invite");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (inv) => {
    if (!confirm(`Revoke access for "${inv.note || inv.code}"? This cannot be undone — the third party will lose access immediately.`)) return;
    try {
      await api.post(`/mediator/invites/${inv.id}/revoke`);
      toast.success("Invite revoked");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const copyLink = async (inv) => {
    const base = (typeof window !== "undefined" && window.location.origin) || "";
    const link = `${base}/mediator-signup?code=${encodeURIComponent(inv.code)}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied to clipboard");
    } catch {
      toast.info(`Invite link: ${link}`);
    }
  };

  const openEditPerms = (inv) => { setEditPermsFor(inv); setEditPerms({ ...(inv.permissions || {}) }); };
  const closeEditPerms = () => { setEditPermsFor(null); setEditPerms({}); };
  const savePerms = async () => {
    try {
      await api.put(`/mediator/invites/${editPermsFor.id}/permissions`, { permissions: editPerms });
      toast.success("Permissions updated");
      closeEditPerms();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openEditScope = (inv) => {
    setEditScopeFor(inv);
    setEditScopeIds(Array.isArray(inv.child_ids) ? [...inv.child_ids] : []);
  };
  const closeEditScope = () => { setEditScopeFor(null); setEditScopeIds([]); };
  const saveScope = async () => {
    try {
      await api.put(`/mediator/invites/${editScopeFor.id}/child-scope`, { child_ids: editScopeIds });
      toast.success("Children updated");
      closeEditScope();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openViews = async (inv) => {
    setViewsFor(inv);
    setViewsLoading(true);
    setViews([]);
    try {
      const { data } = await api.get(`/mediator/invites/${inv.id}/views`);
      setViews(data.views || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load activity log");
    } finally {
      setViewsLoading(false);
    }
  };
  const closeViews = () => { setViewsFor(null); setViews([]); };

  const sortedInvites = useMemo(() => {
    return [...invites].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [invites]);

  return (
    <div className="space-y-6 px-1 sm:px-0" data-testid="third-party-access-page">
      <div className="flex items-center gap-3">
        <Link to="/app/dashboard" className="inline-flex items-center text-sm text-[#6F828A] hover:text-[#2E3A3F]" data-testid="third-party-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
      </div>

      <header className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0">
          <UserCog className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <div className="max-w-2xl">
          <h1 className="font-heading text-2xl font-semibold text-[#2E3A3F] leading-tight">Third-party access</h1>
          <p className="text-sm text-[#6F828A] mt-1">
            Invite a solicitor, mediator, Guardian ad Litem or any professional supporting you. They get a view-only account scoped to the children and records you choose.
          </p>
          <p className="text-xs text-[#116B6E] mt-3 inline-flex items-center gap-1.5 bg-[#D6F0EF] border border-[#B4E0DE] rounded-sm px-2 py-1">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} /> All access is view-only. No actions can be taken within the app.
          </p>
        </div>
      </header>

      {/* Create form */}
      <section className="border border-[#D9E1E3] bg-white rounded-md p-5" data-testid="third-party-create-section">
        <h2 className="font-heading text-lg font-medium text-[#2E3A3F]">Invite a new third party</h2>

        <div className="mt-4 flex items-end gap-3 flex-wrap">
          <div>
            <Label>Access duration (days)</Label>
            <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className="w-[140px]" data-testid="third-party-days-input" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label>Internal note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Solicitor — Smith & Co" data-testid="third-party-note-input" />
          </div>
        </div>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.12em] text-[#1F9FA4] font-semibold">Which children can they see?</div>
          <p className="text-xs text-[#6F828A] mt-1">
            They&apos;ll see no per-child data for any unticked child. You can only grant access to children you manage or co-parent on.
          </p>
          {children.length === 0 ? (
            <div className="mt-3 text-xs text-[#6F828A] italic">
              No children available. Add a child first on the <Link to="/app/children" className="text-[#116B6E] underline">Children page</Link>.
            </div>
          ) : (
            <div className="mt-3 bg-[#F5F7F8] border border-[#D9E1E3] rounded-sm p-3" data-testid="third-party-children-picker">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#D9E1E3]">
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{childIds.length} of {children.length} selected</span>
                <div className="flex gap-2">
                  <button type="button" className="text-xs text-[#116B6E] hover:underline" onClick={() => setChildIds(children.map((c) => c.id))} data-testid="third-party-select-all">Select all</button>
                  <span className="text-[#D9E1E3]">·</span>
                  <button type="button" className="text-xs text-[#825A41] hover:underline" onClick={() => setChildIds([])} data-testid="third-party-clear-all">Clear</button>
                </div>
              </div>
              <div className="space-y-1">
                {children.map((c) => {
                  const checked = childIds.includes(c.id);
                  return (
                    <label key={c.id} className="flex items-center gap-2.5 py-1 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          setChildIds((prev) => v ? [...prev, c.id] : prev.filter((x) => x !== c.id));
                        }}
                        data-testid={`third-party-child-${c.id}`}
                      />
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                      <span className="text-sm text-[#2E3A3F]">{c.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.12em] text-[#1F9FA4] font-semibold">Permissions</div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Core</div>
            <div className="bg-white border border-[#D9E1E3] rounded-sm px-4">
              {CORE_PERMS.map((p) => (
                <PermRow key={p.key} perm={p} value={perms[p.key]} onChange={(v) => setPerms({ ...perms, [p.key]: v })} testid={`third-party-perm-${p.key}`} />
              ))}
            </div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Optional — sensitive</div>
            <div className="bg-white border border-[#E8B8B2]/60 rounded-sm px-4">
              {OPTIONAL_PERMS.map((p) => (
                <PermRow key={p.key} perm={p} value={perms[p.key]} onChange={(v) => setPerms({ ...perms, [p.key]: v })} testid={`third-party-perm-${p.key}`} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={create} disabled={creating} className="bg-[#116B6E] hover:bg-[#0F5658] text-white" data-testid="third-party-create-button">
            {creating ? "Creating…" : "Create invite"}
          </Button>
        </div>
      </section>

      {/* Existing invites list */}
      <section className="border border-[#D9E1E3] bg-white rounded-md p-5" data-testid="third-party-invites-section">
        <h2 className="font-heading text-lg font-medium text-[#2E3A3F]">Your invites</h2>
        <p className="text-sm text-[#6F828A] mt-1">Each row is one third-party access grant. Edit, revoke or view their activity log.</p>

        {loading && <div className="mt-4 text-sm text-[#6F828A]" data-testid="third-party-loading">Loading…</div>}

        {!loading && sortedInvites.length === 0 && (
          <div className="mt-4 bg-[#FBF0DC] border border-[#E8C29A] rounded-sm p-4 text-sm text-[#5C3A1E]" data-testid="third-party-empty">
            You haven't invited anyone yet. Use the form above to create your first invite.
          </div>
        )}

        {!loading && sortedInvites.length > 0 && (
          <ul className="mt-4 space-y-3" data-testid="third-party-invites-list">
            {sortedInvites.map((inv) => {
              const activeCount = ALL_PERMS.filter((p) => (inv.permissions || {})[p.key]).length;
              const childNames = (inv.child_ids || [])
                .map((cid) => (children.find((c) => c.id === cid) || { name: "(removed)" }).name)
                .join(", ");
              return (
                <li
                  key={inv.id}
                  className="border border-[#D9E1E3] rounded-md p-4 bg-[#FAFBFB]"
                  data-testid={`third-party-invite-${inv.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono-data text-base text-[#2E3A3F] tracking-wider">{inv.code}</span>
                        <StatusBadge inv={inv} />
                      </div>
                      {inv.note && <div className="text-xs text-[#3F595B] mt-1 italic">“{inv.note}”</div>}
                      <div className="text-xs text-[#6F828A] mt-1 font-mono-data">
                        Expires {formatDT(inv.expires_at)} · {inv.days}d · {activeCount}/{ALL_PERMS.length} permissions
                      </div>
                      <div className="text-xs text-[#3F595B] mt-1">
                        <span className="text-[#6F828A]">Children: </span>
                        {(inv.child_ids || []).length === 0 ? (
                          <span className="italic text-[#825A41]">none granted</span>
                        ) : (inv.child_ids || []).length === children.length && children.length > 0 ? (
                          <span>All ({inv.child_ids.length})</span>
                        ) : (
                          <span>{childNames}</span>
                        )}
                      </div>
                      {inv.used_by_user_id && (inv.used_by_email || inv.used_by_name) && (
                        <div className="text-xs text-[#116B6E] mt-1">
                          Accepted by <span className="font-medium">{inv.used_by_name || "—"}</span>
                          {inv.used_by_email && <> · <span className="font-mono-data">{inv.used_by_email}</span></>}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {!inv.revoked && !inv.used_by_user_id && (
                      <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => copyLink(inv)} data-testid={`third-party-copy-${inv.id}`}>
                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy invite link
                      </Button>
                    )}
                    {!inv.revoked && (
                      <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => openEditScope(inv)} data-testid={`third-party-edit-scope-${inv.id}`}>
                        <Users className="w-3.5 h-3.5 mr-1.5" /> Children
                      </Button>
                    )}
                    {!inv.revoked && (
                      <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => openEditPerms(inv)} data-testid={`third-party-edit-perms-${inv.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Permissions
                      </Button>
                    )}
                    {inv.used_by_user_id && (
                      <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => openViews(inv)} data-testid={`third-party-views-${inv.id}`}>
                        <ListChecks className="w-3.5 h-3.5 mr-1.5" /> Activity log
                      </Button>
                    )}
                    {!inv.revoked && (
                      <Button size="sm" variant="outline" className="border-[#E8B8B2] text-[#9A2A23] hover:bg-[#FCE9E6] rounded-sm ml-auto" onClick={() => revoke(inv)} data-testid={`third-party-revoke-${inv.id}`}>
                        Revoke
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Permissions edit dialog */}
      <Dialog open={!!editPermsFor} onOpenChange={(v) => !v && closeEditPerms()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="third-party-edit-perms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit permissions</DialogTitle>
            <DialogDescription>Changes apply immediately on the third party's next action.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Core</div>
              <div className="bg-white border border-[#D9E1E3] rounded-sm px-4">
                {CORE_PERMS.map((p) => <PermRow key={p.key} perm={p} value={editPerms[p.key]} onChange={(v) => setEditPerms({ ...editPerms, [p.key]: v })} testid={`third-party-edit-perm-${p.key}`} />)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Optional — sensitive</div>
              <div className="bg-white border border-[#E8B8B2]/60 rounded-sm px-4">
                {OPTIONAL_PERMS.map((p) => <PermRow key={p.key} perm={p} value={editPerms[p.key]} onChange={(v) => setEditPerms({ ...editPerms, [p.key]: v })} testid={`third-party-edit-perm-${p.key}`} />)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEditPerms}>Cancel</Button>
            <Button onClick={savePerms} className="bg-[#116B6E] hover:bg-[#0F5658] text-white">Save permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Children scope edit dialog */}
      <Dialog open={!!editScopeFor} onOpenChange={(v) => !v && closeEditScope()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-md" data-testid="third-party-edit-scope-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit children</DialogTitle>
            <DialogDescription>Changes apply on their next action.</DialogDescription>
          </DialogHeader>
          <div className="bg-white border border-[#D9E1E3] rounded-sm p-3 max-h-[50vh] overflow-y-auto">
            {children.length === 0 ? (
              <div className="text-xs text-[#6F828A] italic">No children available.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#D9E1E3]">
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{editScopeIds.length} of {children.length} selected</span>
                  <div className="flex gap-2">
                    <button type="button" className="text-xs text-[#116B6E] hover:underline" onClick={() => setEditScopeIds(children.map((c) => c.id))}>Select all</button>
                    <span className="text-[#D9E1E3]">·</span>
                    <button type="button" className="text-xs text-[#825A41] hover:underline" onClick={() => setEditScopeIds([])}>Clear</button>
                  </div>
                </div>
                <div className="space-y-1">
                  {children.map((c) => {
                    const checked = editScopeIds.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2.5 py-1 cursor-pointer">
                        <Checkbox checked={checked} onCheckedChange={(v) => setEditScopeIds((prev) => v ? [...prev, c.id] : prev.filter((x) => x !== c.id))} />
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                        <span className="text-sm text-[#2E3A3F]">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEditScope}>Cancel</Button>
            <Button onClick={saveScope} className="bg-[#116B6E] hover:bg-[#0F5658] text-white">Save children</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity log dialog */}
      <Dialog open={!!viewsFor} onOpenChange={(v) => !v && closeViews()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="third-party-views-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Activity log</DialogTitle>
            <DialogDescription>What this third party has actually viewed via this invite. Useful for solicitor / court-attestation purposes.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {viewsLoading && <div className="text-sm text-[#6F828A] py-6 text-center">Loading activity…</div>}
            {!viewsLoading && views.length === 0 && (
              <div className="bg-[#FBF0DC] border border-[#E8C29A] rounded-sm p-4 text-sm text-[#5C3A1E]">
                No activity recorded yet. Once they open shared records, you'll see entries here.
              </div>
            )}
            {!viewsLoading && views.length > 0 && (
              <ul className="divide-y divide-[#D9E1E3] border border-[#D9E1E3] rounded-sm">
                {views.map((v) => (
                  <li key={v.id} className="px-3 py-2.5">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <span className="font-medium text-[#2E3A3F] text-sm">{v.resource_label || v.resource_type}</span>
                      <span className="text-[10px] uppercase tracking-[0.06em] text-[#6F828A] font-mono-data flex-shrink-0">{v.resource_type.replace("_", " ")}</span>
                    </div>
                    <div className="text-[11px] text-[#6F828A] mt-0.5 font-mono-data">
                      {formatDT(v.viewed_at)}{v.ip && <> · {v.ip}</>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeViews} className="border-[#D9E1E3]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
